/**
 * Shared "send this somewhere" helpers.
 *
 * Before this module the app had two hand-rolled share implementations that
 * had drifted apart (the water-source drawer and the forecast share card),
 * and no WhatsApp path at all. Everything that shares a place, a person or a
 * record now goes through `buildShareText` + `ShareSheet`, so the payloads
 * look alike wherever they land.
 *
 * On the WhatsApp question: `navigator.share` stays the primary path. It
 * already lists WhatsApp among its targets on mobile, it is the only path
 * that can attach a photo, and FPA volunteers are not all on WhatsApp — so
 * hard-wiring the one channel would be a regression. `whatsappUrl` backs the
 * explicit WhatsApp button, which also replaces the clipboard-and-alert
 * fallback on desktop, where `navigator.share` is frequently absent.
 */

import client from '@/api/client';

export interface ShareLocation {
  lat: number;
  lng: number;
  label?: string;
}

/** Name + number. Deliberately no email — see `SharePayload.contacts`. */
export interface ShareContact {
  name?: string | null;
  phone?: string | null;
}

export interface SharePayload {
  /** `navigator.share` title, and the sheet heading. */
  title: string;
  /**
   * What KIND of thing this is — "Water source", "Property", "Dropped pin".
   * Prefixed onto the first line so a message landing in WhatsApp says what
   * it is before it says which one, rather than opening with a bare name.
   */
  kind?: string;
  /** Optional first line, e.g. "Reservoir 4" or "J. Botha". */
  headline?: string;
  /** Label/value rows. Empty values are dropped, so callers can pass freely. */
  details?: Array<[label: string, value: string | number | null | undefined]>;
  /**
   * Drives the Directions BUTTON only. It is not written into the text —
   * put a `['Directions', directionsUrl(lat, lng)]` row in `details` where
   * you want it to read, since the right position differs per record.
   */
  location?: ShareLocation | null;
  /**
   * Rendered under a "Contacts:" heading. Entries with only a name are kept
   * (still useful); entirely empty entries are dropped.
   */
  contacts?: ShareContact[];
  /** Free lines appended last. */
  extraLines?: string[];
  /**
   * Attached on the native path only. Must already be resolved — see
   * `fetchShareFile` and the note there about user activation.
   */
  file?: File | null;
}

export type ShareOutcome = 'shared' | 'copied' | 'cancelled' | 'failed';

/** Universal Google Maps directions link — works as a plain link anywhere. */
export function directionsUrl(lat: number, lng: number): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
}

/**
 * Open turn-by-turn directions in the platform's own maps app.
 *
 * Moved verbatim from WaterSourceDetailDrawer — the per-platform URL shapes
 * are load-bearing and the comments explain the non-obvious parts.
 */
export function openDirections(lat: number, lng: number, name: string): void {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isAndroid = /Android/.test(ua);
  if (isIOS) {
    // ll= pins the exact coordinates; q= is the label shown on the pin, not a search query.
    window.open(`maps://?ll=${lat},${lng}&q=${encodeURIComponent(name)}`, '_blank');
  } else if (isAndroid) {
    // geo:lat,lng?q=lat,lng(label) routes to the exact coordinates with a named pin.
    // Using ?q=name alone causes Google Maps to search by name instead of coordinates.
    window.open(`geo:${lat},${lng}?q=${lat},${lng}(${encodeURIComponent(name)})`, '_blank');
  } else {
    window.open(directionsUrl(lat, lng), '_blank');
  }
}

/**
 * Normalise a South African number to the international digits-only form
 * wa.me expects. Returns null when there is nothing usable, in which case the
 * caller falls back to the no-recipient chooser.
 */
export function normalisePhone(phone: string | null | undefined): string | null {
  if (!phone) return null;
  let digits = String(phone).replace(/[^\d+]/g, '');
  if (digits.startsWith('+')) digits = digits.slice(1);
  digits = digits.replace(/\D/g, '');
  if (!digits) return null;
  // Local SA format: 0XX XXX XXXX -> 27XXXXXXXXX.
  if (digits.length === 10 && digits.startsWith('0')) return `27${digits.slice(1)}`;
  return digits;
}

/** wa.me deep link. Without a phone this opens WhatsApp's contact chooser. */
export function whatsappUrl(text: string, phone?: string | null): string {
  const to = normalisePhone(phone);
  const q = `?text=${encodeURIComponent(text)}`;
  return to ? `https://wa.me/${to}${q}` : `https://wa.me/${q}`;
}

export function openWhatsApp(text: string, phone?: string | null): void {
  window.open(whatsappUrl(text, phone), '_blank', 'noopener');
}

/** Average of every vertex across every ring. Good enough to drop a map pin. */
export function polygonCentroid(
  rings: Array<Array<[number, number]>> | null | undefined,
): [number, number] | null {
  if (!rings || rings.length === 0) return null;
  let sumLat = 0;
  let sumLng = 0;
  let n = 0;
  for (const ring of rings) {
    for (const point of ring) {
      const [lat, lng] = point;
      if (typeof lat !== 'number' || typeof lng !== 'number') continue;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
      sumLat += lat;
      sumLng += lng;
      n += 1;
    }
  }
  if (n === 0) return null;
  return [sumLat / n, sumLng / n];
}

/**
 * Render a payload as the plain text that actually gets sent.
 *
 * Order is exactly: headline, `details` as given, contacts, `extraLines`.
 * Nothing is inserted implicitly — `details` is the caller's layout.
 */
export function buildShareText(p: SharePayload): string {
  const lines: string[] = [];
  const head = [p.kind, p.headline].filter(Boolean).join(': ');
  if (head) lines.push(head);

  for (const [label, value] of p.details ?? []) {
    if (value === null || value === undefined || value === '') continue;
    lines.push(`${label}: ${value}`);
  }


  const contacts = (p.contacts ?? []).filter((c) => c?.name || c?.phone);
  if (contacts.length > 0) {
    lines.push('Contacts:');
    for (const c of contacts) {
      const name = (c.name || '').trim();
      const phone = (c.phone || '').trim();
      lines.push(`  ${[name, phone].filter(Boolean).join(' — ')}`);
    }
  }

  for (const extra of p.extraLines ?? []) {
    if (extra) lines.push(extra);
  }

  return lines.join('\n');
}

export function canNativeShare(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

/**
 * Pull an image into a File so it can ride along on the native share.
 *
 * Call this ahead of time, NOT inside the click handler: `navigator.share`
 * needs transient user activation, and awaiting a network round-trip first
 * spends it (iOS Safari then throws NotAllowedError).
 *
 * Takes an API path, not an absolute URL, and goes through the axios client:
 * media sits behind the JWT authorizer, and a bare fetch() carries no token,
 * so the attachment silently never materialised.
 */
export async function fetchShareFile(path: string, name: string): Promise<File | null> {
  try {
    const res = await client.get(path, { responseType: 'blob' });
    const blob = res.data as Blob;
    const ext = (blob.type.split('/')[1] || 'jpg').replace(/[^a-z0-9]/gi, '');
    return new File([blob], `${name}.${ext}`, { type: blob.type });
  } catch {
    return null;
  }
}

/**
 * Native share sheet. Tries with the attached file first, then text-only.
 *
 * A user cancelling reports 'cancelled', never 'failed' — callers must not
 * treat it as an error or quietly fall through to the clipboard.
 */
export async function nativeShare(p: SharePayload): Promise<ShareOutcome> {
  if (!canNativeShare()) return 'failed';
  const text = buildShareText(p);

  if (p.file && navigator.canShare?.({ files: [p.file] })) {
    try {
      await navigator.share({ title: p.title, text, files: [p.file] });
      return 'shared';
    } catch (err: unknown) {
      if ((err as { name?: string })?.name === 'AbortError') return 'cancelled';
      // Sharing with a file can fail on its own — fall through to text-only.
    }
  }

  try {
    // The directions link is already inside `text`; passing it again as `url`
    // makes the share sheet render the link twice.
    await navigator.share({ title: p.title, text });
    return 'shared';
  } catch (err: unknown) {
    if ((err as { name?: string })?.name === 'AbortError') return 'cancelled';
    return 'failed';
  }
}

export async function copyShareText(p: SharePayload): Promise<ShareOutcome> {
  try {
    await navigator.clipboard.writeText(buildShareText(p));
    return 'copied';
  } catch {
    return 'failed';
  }
}
